import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { closeTooltip, openTooltip } from '../../state/imperativeApis/tooltipApi'
import { buildTooltipAnchorProps } from 'components/shared/tooltipContract'
import cn from 'classnames'
import { isMobile } from 'lib/device'

const setRef = (ref, value) => {
    if (!ref) return
    if (typeof ref === 'function') {
        ref(value)
        return
    }
    ref.current = value
}

const mergeRefs =
    (...refs) =>
    value => {
        refs.forEach(ref => setRef(ref, value))
    }

/**
 * @typedef {{
 *   disable?: boolean,
 *   asChild?: boolean,
 *   children?: React.ReactNode,
 *   contextStyles?: string,
 *   tip?: string,
 *   html?: boolean,
 *   place?: import('react-tooltip').PlacesType,
 *   offset?: number,
 *   [key: string]: unknown,
 * }} WithTooltipProps
 */

const WithTooltip = forwardRef(
    /** @returns {React.ReactElement | null} */
    function WithTooltip(/** @type {WithTooltipProps} */ props, ref) {
        const { asChild, children, contextStyles, tip, html, place, offset } = props
        const tipped = useRef(null)

        // expose showTooltip/hideTooltip methods to parent via ref
        useImperativeHandle(ref, () => ({
            showTooltip: () =>
                openTooltip(tipped.current, {
                    content: tip,
                    place: place || 'bottom',
                }),
            hideTooltip: () => closeTooltip(tipped.current),
        }))

        useEffect(() => {
            if (!props.disable)
                return () => {
                    closeTooltip(tipped.current)
                }
        }, [props.disable])

        if (props.disable || isMobile) {
            return /** @type {React.ReactElement} */ (props.children)
        }

        const anchorProps = buildTooltipAnchorProps({
            content: tip,
            allowHtml: html,
            place: place || 'bottom',
            offset: offset || 0,
        })
        const tooltipProps = {
            className: cn('with-tool-tip-comp', contextStyles),
            ...anchorProps,
        }

        if (asChild && React.Children.count(children) === 1) {
            const child = React.Children.only(children)

            if (React.isValidElement(child)) {
                const childWithRef =
                    /** @type {React.ReactElement & { ref?: React.Ref<unknown> }} */ (child)

                return React.cloneElement(child, {
                    ...tooltipProps,
                    ref: mergeRefs(childWithRef.ref, tipped),
                    className: cn(child.props.className, tooltipProps.className),
                })
            }
        }

        return (
            <span ref={tipped} {...tooltipProps}>
                {children}
            </span>
        )
    },
)

export default WithTooltip
